import { ComponentPropsWithoutRef } from "react";
import { Table as TanstackTable } from "@tanstack/react-table";
import { TableBody } from "../../base/TableBody";
import { DataTableBodyRow } from "./DataTableBodyRow";
import { useColumnOrder } from "./useColumnOrder";

/**
 * Props for the DataTableBody component
 * @extends {ComponentPropsWithoutRef<"div">}
 */
export type DataTableBodyProps<T> = {
  /**
   * The table instance from @tanstack/react-table to render
   */
  table: TanstackTable<T>;
  /**
   * Whether the table has sub rows
   */
  hasSubRows: boolean;
} & ComponentPropsWithoutRef<"div">;

export const DataTableBodyContent = <T,>({
  table,
  hasSubRows,
}: {
  table: TanstackTable<T>;
  hasSubRows: boolean;
}) => {
  const { columnOrder } = useColumnOrder({ table });
  return table
    .getRowModel()
    .rows.map((row, rowIndex) => (
      <DataTableBodyRow<T>
        columnOrder={columnOrder}
        key={row.id}
        row={row}
        rowIndex={rowIndex + table.getHeaderGroups().length}
        tableHasSubRows={hasSubRows}
      />
    ));
};

export function DataTableBody<T>(props: DataTableBodyProps<T>) {
  const { table, hasSubRows, ...rest } = props;
  return (
    <TableBody type="data-table" {...rest}>
      <DataTableBodyContent table={table} hasSubRows={hasSubRows} />
    </TableBody>
  );
}
